import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const STATUT_BADGE = {
  EN_COURS:    { label: 'En cours',   cls: 'badge-info' },
  A_VERIFIER:  { label: 'À vérifier', cls: 'badge-warning' },
  VALIDE:      { label: 'Validé',     cls: 'badge-success' },
  REJETE:      { label: 'Rejeté',     cls: 'badge-error' },
};

function ScoreBar({ score }) {
  if (score == null) return <span style={{ color: '#999' }}>—</span>;
  const cls = score >= 85 ? 'high' : score >= 70 ? 'medium' : 'low';
  return (
    <div className="score-bar">
      <div className="score-bar-track">
        <div className={`score-bar-fill ${cls}`} style={{ width: `${score}%` }} />
      </div>
      <span style={{ fontSize: '0.78rem', fontWeight: 600, minWidth: 36 }}>{score.toFixed(0)}%</span>
    </div>
  );
}

export default function Bilans() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bilans, setBilans]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showImport, setShowImport] = useState(false);
  const [structures, setStructures] = useState([]);
  const [importForm, setImportForm] = useState({ structure_id: '', exercice: new Date().getFullYear() - 1 });
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting]   = useState(false);
  const [importMsg, setImportMsg]   = useState(null);
  const fileRef = useRef();

  useEffect(() => { loadBilans(); }, []);

  const loadBilans = async () => {
    try {
      const { data } = await api.get('/bilans/');
      setBilans(data);
    } catch (e) {} finally { setLoading(false); }
  };

  const loadStructures = async () => {
    try {
      const { data: adherentsList } = await api.get('/adherents/');
      const all = [];
      for (const a of adherentsList) {
        try {
          const { data: structs } = await api.get(`/adherents/${a.id}/structures`);
          structs.forEach(s => all.push({ ...s, adherent_nom: a.nom_commercial }));
        } catch (e) {}
      }
      setStructures(all);
    } catch (e) {
      console.error('Erreur chargement structures:', e);
    }
  };

  const openImport = () => { setShowImport(true); loadStructures(); setImportMsg(null); };

  const handleImport = async (e) => {
    e.preventDefault();
    if (!importFile) return;
    setImporting(true);
    setImportMsg(null);
    try {
      const fd = new FormData();
      fd.append('structure_id', importForm.structure_id);
      fd.append('exercice', importForm.exercice);
      fd.append('fichier', importFile);
      setImportMsg({ type: 'info', text: '⏳ Analyse IA en cours... (30-60 secondes)' });
      await api.post('/bilans/import', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
      setShowImport(false);
      setImportMsg(null);
      setImportFile(null);
      loadBilans();
      let attempts = 0;
      const poll = setInterval(() => {
        attempts++;
        loadBilans();
        if (attempts >= 24) clearInterval(poll);
      }, 5000);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erreur lors de l\'import';
      setImportMsg({ type: 'error', text: detail });
    } finally {
      setImporting(false);
    }
  };

  const handleValider = async (id) => {
    if (!window.confirm('Valider ce bilan ?')) return;
    try {
      await api.post(`/bilans/${id}/valider`);
      loadBilans();
    } catch (e) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer définitivement ce bilan ?')) return;
    try {
      await api.delete(`/bilans/${id}`);
      loadBilans();
    } catch (e) {}
  };

  const canImport = user.role === 'GESTIONNAIRE' || user.role === 'ADMIN';
  const canValidate = canImport;

  return (
    <div>
      <div className="topbar">
        <div className="topbar-title">Bilans comptables</div>
        {canImport && (
          <button className="btn btn-primary" onClick={openImport}>📤 Importer un bilan</button>
        )}
      </div>
      <div className="page-content">

        {/* Modal import */}
        {showImport && (
          <div className="modal-overlay">
            <div className="modal-box" style={{width:480}}>
              <div className="modal-header">
                <span className="modal-title">Importer un bilan</span>
                <button onClick={() => setShowImport(false)} className="modal-close">✕</button>
              </div>
              <div className="modal-body">
                {importMsg && <div className={`alert alert-${importMsg.type}`}>{importMsg.text}</div>}
                <form onSubmit={handleImport}>
                  <div className="form-group">
                    <label className="form-label">Structure</label>
                    <select className="form-input" value={importForm.structure_id}
                      onChange={e => setImportForm({ ...importForm, structure_id: e.target.value })} required>
                      <option value="">— Sélectionner —</option>
                      {structures.map(s => (
                        <option key={s.id} value={s.id}>{s.adherent_nom} — {s.raison_sociale}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Exercice</label>
                    <input className="form-input" type="number" min="2000" max="2099"
                      value={importForm.exercice}
                      onChange={e => setImportForm({ ...importForm, exercice: parseInt(e.target.value) })} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fichier (PDF, Excel, Word)</label>
                    <input ref={fileRef} type="file" accept=".pdf,.xlsx,.xls,.docx"
                      onChange={e => setImportFile(e.target.files[0])}
                      style={{ display: 'block', marginTop: 6, fontSize: '0.85rem' }} required />
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-primary" type="submit" disabled={importing}>
                      {importing ? 'Import en cours...' : '📤 Lancer l\'import'}
                    </button>
                    <button className="btn btn-outline" type="button" onClick={() => setShowImport(false)}>Annuler</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Tableau des bilans */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--ecf-mid-grey)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div className="card-title" style={{ margin: 0 }}>Liste des bilans ({bilans.length})</div>
          </div>
          {loading ? (
            <div style={{ padding: 24, color: '#999' }}>Chargement...</div>
          ) : bilans.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999' }}>
              Aucun bilan importé.{canImport && <> <button className="btn btn-primary" style={{ marginLeft: 12 }} onClick={openImport}>Importer le premier bilan</button></>}
            </div>
          ) : (
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Structure</th>
                    <th>Exercice</th>
                    <th>Période</th>
                    <th>Source</th>
                    <th>Score</th>
                    <th>Statut</th>
                    <th>Importé le</th>
                    {canValidate && <th>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {bilans.map(b => {
                    const badge = STATUT_BADGE[b.statut] || { label: b.statut, cls: 'badge-grey' };
                    return (
                      <tr key={b.id}>
                        <td className="important">
                            {b.fichier_nom || b.structure_id.slice(0, 8) + '…'}
                            {b.transfert_id && <span className="badge badge-warning" style={{ marginLeft: 6, fontSize: '0.55rem' }}>Transféré</span>}
                          </td>
                          <td>{b.exercice}</td>
                          <td style={{ fontSize: '0.75rem', color: 'var(--ecf-grey)', whiteSpace: 'nowrap' }}>
                            {b.date_debut && b.date_fin
                              ? `${new Date(b.date_debut).toLocaleDateString('fr-FR')} → ${new Date(b.date_fin).toLocaleDateString('fr-FR')}`
                              : '—'}
                          </td>
                          <td><span className="badge badge-grey">{b.source_extraction || '—'}</span></td>
                        <td style={{ minWidth: 120 }}><ScoreBar score={b.score_confiance} /></td>
                        <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                        <td>{b.created_at ? new Date(b.created_at).toLocaleDateString('fr-FR') : '—'}</td>
                        {canValidate && (
                          <td>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                onClick={() => navigate(`/bilans/${b.id}`)}>Détail</button>
                              {b.statut === 'A_VERIFIER' && (
                                <button className="btn btn-primary" style={{ padding: '4px 10px', fontSize: '0.75rem' }}
                                  onClick={() => handleValider(b.id)}>Valider</button>
                              )}
                              <button className="btn btn-outline" style={{ padding: '4px 10px', fontSize: '0.75rem', color: 'var(--ecf-error)' }}
                                onClick={() => handleDelete(b.id)}>Supprimer</button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
